// Landing pública de Convokko (/home). Parte de la propuesta web original
// (hero, solución, panel, actividades, planificación, beneficios y llamada
// final) con la marca y las funciones actuales de la app. Accesible con o sin
// sesión iniciada.
//
// Sistema visual: fondo claro con dos bloques navy (hero y demo), un único
// acento ámbar. Radios: paneles 28px, tarjetas 20px, piezas internas 12px,
// botones en píldora. Las vistas previas usan componentes reales de la app
// (pista de fútbol sala, mensaje de convocatoria) con datos de ejemplo.
import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import ScheduleIcon from "@mui/icons-material/Schedule";
import StorageIcon from "@mui/icons-material/Storage";
import InsightsIcon from "@mui/icons-material/Insights";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import SyncIcon from "@mui/icons-material/Sync";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HistoryIcon from "@mui/icons-material/History";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import SportsVolleyballIcon from "@mui/icons-material/SportsVolleyball";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from "chart.js";
import { Bar } from "react-chartjs-2";

import logoConvokko from "assets/images/landing/logo.png";
import silbato from "assets/images/landing/silbato.png";
import heroImg from "assets/images/landing/hero.jpg";
import actividadesImg from "assets/images/landing/actividades.jpg";
import equipoImg from "assets/images/landing/equipo.jpg";
import { MedioCampoFutsal, MensajeWhatsApp, useNotify } from "components/Convokko";
import { SLOTS_FUTSAL } from "utils/alineacion";
import { construirMensajeConvocatoria } from "utils/convocatoria";
import { API_BASE_URL } from "config";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const NAVY = "#1C2A55";
const NAVY_DEEP = "#131D3D";
const AMBER = "#FBB53F";
const AMBER_DARK = "#B45309";
const INK = "#161E33";
const MUTED = "#545D72";
const PAGE = "#F6F7FA";
const LINE = "#E3E6EE";
const EMAIL_CONTACTO = "convokko@gmail.com";
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

const contenedor = { maxWidth: 1240, mx: "auto", px: { xs: 2, sm: 3, md: 4 } };

const tieneSesion = () => {
  try {
    const token = localStorage.getItem("token");
    return !!token && token !== "undefined" && token !== "null" && token.length >= 10;
  } catch {
    return false;
  }
};

const irA = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ---------------------------------------------------------------- movimiento

// Aparición suave al entrar en pantalla (IntersectionObserver, una sola vez).
// Con "reducir movimiento" activado el contenido se muestra directamente.
function Reveal({ children, delay, sx }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        minWidth: 0,
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(28px)",
        transition: `opacity .8s ${EASE} ${delay}ms, transform .8s ${EASE} ${delay}ms`,
        "@media (prefers-reduced-motion: reduce)": {
          opacity: 1,
          transform: "none",
          transition: "none",
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

Reveal.propTypes = {
  children: PropTypes.node.isRequired,
  delay: PropTypes.number,
  sx: PropTypes.object,
};
Reveal.defaultProps = { delay: 0, sx: {} };

// ---------------------------------------------------------------- piezas comunes

const boton = {
  height: 52,
  px: 3.25,
  borderRadius: 999,
  textTransform: "none",
  fontSize: "1rem",
  fontWeight: 700,
  letterSpacing: 0,
  boxShadow: "none",
  whiteSpace: "nowrap",
  transition: `transform .2s ${EASE}, background-color .2s ease, border-color .2s ease`,
  "&:active": { transform: "scale(0.97)" },
};

const botonAmbar = {
  ...boton,
  color: NAVY_DEEP,
  bgcolor: AMBER,
  "&:hover": { bgcolor: "#F9A825", boxShadow: "none" },
  "& .MuiButton-endIcon": { transition: `transform .25s ${EASE}` },
  "&:hover .MuiButton-endIcon": { transform: "translateX(3px)" },
};

const botonFantasma = {
  ...boton,
  color: "#fff",
  border: "1.5px solid rgba(255,255,255,0.35)",
  "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.06)" },
};

function Titulo({ children, claro, sx }) {
  return (
    <Typography
      component="h2"
      sx={{
        fontSize: { xs: "2rem", md: "2.75rem" },
        fontWeight: 800,
        lineHeight: 1.08,
        letterSpacing: "-0.035em",
        color: claro ? "#fff" : INK,
        maxWidth: "20ch",
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

Titulo.propTypes = {
  children: PropTypes.node.isRequired,
  claro: PropTypes.bool,
  sx: PropTypes.object,
};

function Texto({ children, claro, sx }) {
  return (
    <Typography
      sx={{
        fontSize: { xs: "1rem", md: "1.0625rem" },
        lineHeight: 1.65,
        color: claro ? "rgba(255,255,255,0.74)" : MUTED,
        maxWidth: "60ch",
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

Texto.propTypes = {
  children: PropTypes.node.isRequired,
  claro: PropTypes.bool,
  sx: PropTypes.object,
};

function Etiqueta({ children, claro }) {
  return (
    <Typography
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: claro ? AMBER : AMBER_DARK,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  );
}

Etiqueta.propTypes = { children: PropTypes.node.isRequired, claro: PropTypes.bool };

function Icono({ children, tono }) {
  const tonos = {
    ambar: { bg: "rgba(251,181,63,0.18)", fg: AMBER_DARK },
    navy: { bg: "rgba(28,42,85,0.08)", fg: NAVY },
    claro: { bg: "rgba(255,255,255,0.12)", fg: AMBER },
    oscuro: { bg: "rgba(19,29,61,0.12)", fg: NAVY_DEEP },
  };
  const t = tonos[tono];
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "12px",
        bgcolor: t.bg,
        color: t.fg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        "& svg": { fontSize: 24 },
      }}
    >
      {children}
    </Box>
  );
}

Icono.propTypes = { children: PropTypes.node.isRequired, tono: PropTypes.string };
Icono.defaultProps = { tono: "ambar" };

// Aviso tal y como llega al móvil del entrenador.
function AvisoPush({ sx }) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        bgcolor: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(16px)",
        borderRadius: "16px",
        p: 1.5,
        pr: 2,
        boxShadow: "0 18px 40px -12px rgba(19,29,61,0.45)",
        border: "1px solid rgba(255,255,255,0.7)",
        maxWidth: 330,
        ...sx,
      }}
    >
      <Box
        component="img"
        src={silbato}
        alt=""
        sx={{
          width: 36,
          height: 36,
          objectFit: "contain",
          borderRadius: "10px",
          bgcolor: PAGE,
          p: 0.5,
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 800, color: INK, lineHeight: 1.3 }}>
          Entrenamiento a punto de empezar
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: MUTED, lineHeight: 1.35 }}>
          El entreno del Alevín A empieza a las 17:30. Ya puedes pasar asistencia.
        </Typography>
      </Box>
    </Stack>
  );
}

AvisoPush.propTypes = { sx: PropTypes.object };
AvisoPush.defaultProps = { sx: {} };

// ---------------------------------------------------------------- cabecera

function Cabecera({ conSesion }) {
  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        bgcolor: "rgba(246,247,250,0.82)",
        backdropFilter: "saturate(180%) blur(14px)",
        borderBottom: `1px solid ${LINE}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ ...contenedor, height: 68 }}
      >
        <Box
          component="a"
          href="/home"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          sx={{ display: "flex", alignItems: "center" }}
          aria-label="Convokko, volver arriba"
        >
          <Box
            component="img"
            src={logoConvokko}
            alt="Convokko"
            sx={{ height: { xs: 26, sm: 30 } }}
          />
        </Box>
        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, md: 1 }}>
          {[
            ["Funciones", "funciones"],
            ["Cómo funciona", "como-funciona"],
            ["Beneficios", "beneficios"],
          ].map(([texto, id]) => (
            <Button
              key={id}
              onClick={() => irA(id)}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                color: MUTED,
                borderRadius: 999,
                px: 1.75,
                "&:hover": { color: INK, bgcolor: "rgba(28,42,85,0.05)" },
              }}
            >
              {texto}
            </Button>
          ))}
          <Button
            href={conSesion ? "/dashboard" : "/authentication/sign-in"}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.95rem",
              color: INK,
              borderRadius: 999,
              px: 1.75,
              "&:hover": { bgcolor: "rgba(28,42,85,0.05)" },
            }}
          >
            {conSesion ? "Mi panel" : "Acceder"}
          </Button>
          <Button
            onClick={() => irA("demo")}
            sx={{
              ...boton,
              height: 40,
              px: 2.25,
              fontSize: "0.9rem",
              color: "#fff",
              bgcolor: NAVY,
              "&:hover": { bgcolor: NAVY_DEEP, boxShadow: "none" },
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Solicitar demo
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

Cabecera.propTypes = { conSesion: PropTypes.bool.isRequired };

// ---------------------------------------------------------------- hero

function Hero() {
  const entra = (delay) => ({
    animation: `landingEntra .9s ${EASE} ${delay}ms both`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  });

  return (
    <Box component="section" sx={{ ...contenedor, pt: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: "24px", md: "28px" },
          bgcolor: NAVY,
          backgroundImage: `radial-gradient(120% 90% at 0% 0%, #26386F 0%, ${NAVY} 45%, ${NAVY_DEEP} 100%)`,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.08fr 1fr" },
          minHeight: { md: "min(680px, calc(100dvh - 100px))" },
          "@keyframes landingEntra": {
            from: { opacity: 0, transform: "translateY(24px)" },
            to: { opacity: 1, transform: "none" },
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 6, md: 8 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box sx={entra(0)}>
            <Etiqueta claro>Para escuelas deportivas</Etiqueta>
          </Box>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "2.5rem", sm: "3.25rem", lg: "4rem" },
              fontWeight: 800,
              lineHeight: 1.02,
              letterSpacing: "-0.045em",
              color: "#fff",
              mb: 3,
              ...entra(80),
            }}
          >
            Gestión integral para{" "}
            <Box component="span" sx={{ color: AMBER }}>
              escuelas deportivas
            </Box>
          </Typography>
          <Texto
            claro
            sx={{
              fontSize: { xs: "1.05rem", md: "1.175rem" },
              maxWidth: "44ch",
              mb: 4.5,
              ...entra(160),
            }}
          >
            Del caos del papel al orden digital. Más tiempo para lo que importa: desarrollar talento
            deportivo.
          </Texto>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={entra(240)}>
            <Button onClick={() => irA("demo")} sx={botonAmbar} endIcon={<ArrowForwardIcon />}>
              Solicitar demo
            </Button>
            <Button onClick={() => irA("funciones")} sx={botonFantasma}>
              Ver funciones
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            position: "relative",
            minHeight: { xs: 340, sm: 420, md: "auto" },
            p: { xs: 1.25, md: 1.5 },
            pl: { md: 0 },
          }}
        >
          <Box
            component="img"
            src={heroImg}
            alt="Entrenador consultando Convokko en una tableta durante un partido"
            fetchpriority="high"
            sx={{
              position: { xs: "absolute", md: "absolute" },
              inset: { xs: 10, md: "12px 12px 12px 0" },
              width: { xs: "calc(100% - 20px)", md: "calc(100% - 12px)" },
              height: { xs: "calc(100% - 20px)", md: "calc(100% - 24px)" },
              objectFit: "cover",
              objectPosition: "68% center",
              borderRadius: { xs: "16px", md: "20px" },
              ...entra(120),
            }}
          />
          <AvisoPush
            sx={{
              position: "absolute",
              left: { xs: 22, md: -36 },
              right: { xs: 22, md: "auto" },
              bottom: { xs: 26, md: 64 },
              ...entra(700),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// Franja bajo el hero: deportes y competición que cubre hoy la app.
function Cobertura() {
  const items = [
    { icono: <SportsSoccerIcon />, texto: "Fútbol sala" },
    { icono: <SportsVolleyballIcon />, texto: "Voleibol" },
    { icono: <SyncIcon />, texto: "Sincronización con tu liga" },
    { icono: <NotificationsActiveIcon />, texto: "Avisos en el móvil" },
  ];
  return (
    <Box sx={{ ...contenedor, py: { xs: 4, md: 5 } }}>
      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        sx={{ columnGap: { xs: 3, md: 6 }, rowGap: 1.5 }}
      >
        {items.map((i) => (
          <Stack
            key={i.texto}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ color: MUTED }}
          >
            <Box sx={{ display: "flex", color: NAVY, "& svg": { fontSize: 20 } }}>{i.icono}</Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600 }}>{i.texto}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------- bento

const ASISTENCIA_EJEMPLO = {
  labels: ["Sep", "Oct", "Nov", "Dic", "Ene", "Feb", "Mar", "Abr"],
  data: [71, 78, 74, 69, 81, 84, 79, 88],
};

function GraficoAsistencia() {
  const data = useMemo(
    () => ({
      labels: ASISTENCIA_EJEMPLO.labels,
      datasets: [
        {
          data: ASISTENCIA_EJEMPLO.data,
          backgroundColor: ASISTENCIA_EJEMPLO.data.map((_, i) =>
            i === ASISTENCIA_EJEMPLO.data.length - 1 ? AMBER : "rgba(28,42,85,0.16)"
          ),
          hoverBackgroundColor: AMBER,
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 34,
        },
      ],
    }),
    []
  );
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 900 },
      plugins: {
        legend: { display: false },
        tooltip: {
          displayColors: false,
          callbacks: { label: (ctx) => `${ctx.parsed.y}% de asistencia` },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { color: MUTED, font: { family: "Plus Jakarta Sans", size: 12, weight: "600" } },
        },
        y: { display: false, min: 50, max: 92 },
      },
    }),
    []
  );
  return (
    <Box
      sx={{ height: { xs: 170, md: 200 } }}
      aria-label="Gráfico de ejemplo de asistencia mensual"
    >
      <Bar data={data} options={options} />
    </Box>
  );
}

const celda = {
  borderRadius: "20px",
  p: { xs: 3, md: 3.5 },
  height: "100%",
  display: "flex",
  flexDirection: "column",
};

function TituloCelda({ children, claro }) {
  return (
    <Typography
      component="h3"
      sx={{
        fontSize: "1.25rem",
        fontWeight: 800,
        letterSpacing: "-0.02em",
        color: claro ? "#fff" : INK,
        mt: 2.5,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  );
}

TituloCelda.propTypes = { children: PropTypes.node.isRequired, claro: PropTypes.bool };

function Funciones() {
  return (
    <Box
      component="section"
      id="funciones"
      sx={{ ...contenedor, py: { xs: 8, md: 12 }, scrollMarginTop: 80 }}
    >
      <Reveal>
        <Titulo sx={{ mb: 2 }}>Toda tu escuela en un solo sitio</Titulo>
        <Texto sx={{ mb: { xs: 5, md: 7 } }}>
          Coordinadores y entrenadores trabajan sobre la misma información, cada uno con lo que le
          corresponde.
        </Texto>
      </Reveal>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(6, 1fr)" },
          gap: { xs: 2, md: 2.5 },
        }}
      >
        {/* Panel de control: celda grande */}
        <Reveal sx={{ gridColumn: { sm: "1 / -1", md: "span 4" }, gridRow: { md: "span 2" } }}>
          <Box sx={{ ...celda, bgcolor: "#fff", border: `1px solid ${LINE}` }}>
            <Icono tono="navy">
              <InsightsIcon />
            </Icono>
            <TituloCelda>Panel de control con métricas reales</TituloCelda>
            <Texto sx={{ fontSize: "1rem", mb: 3 }}>
              Asistencia y participación de cada jugador y equipo. Filtra por equipo, jugador o día
              y decide con datos, no con impresiones.
            </Texto>
            <Box sx={{ mt: "auto", bgcolor: PAGE, borderRadius: "12px", p: { xs: 2, md: 2.5 } }}>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: INK, mb: 1.5 }}>
                Asistencia a entrenamientos
              </Typography>
              <GraficoAsistencia />
            </Box>
          </Box>
        </Reveal>

        {/* Acceso por roles */}
        <Reveal delay={80} sx={{ gridColumn: { md: "span 2" } }}>
          <Box sx={{ ...celda, bgcolor: NAVY }}>
            <Icono tono="claro">
              <AdminPanelSettingsIcon />
            </Icono>
            <TituloCelda claro>Acceso por roles</TituloCelda>
            <Texto claro sx={{ fontSize: "0.98rem" }}>
              El coordinador gestiona toda la escuela. Cada entrenador ve solo sus equipos.
            </Texto>
          </Box>
        </Reveal>

        {/* Sincronización con la competición */}
        <Reveal delay={140} sx={{ gridColumn: { md: "span 2" } }}>
          <Box sx={{ ...celda, bgcolor: AMBER }}>
            <Icono tono="oscuro">
              <SyncIcon />
            </Icono>
            <TituloCelda>Sincronizado con tu competición</TituloCelda>
            <Texto sx={{ fontSize: "0.98rem", color: "rgba(19,29,61,0.78)" }}>
              Partidos y clasificaciones llegan solos desde la web oficial de la liga. Ya conectado
              con el IMD de Sevilla y adaptable a otras competiciones.
            </Texto>
          </Box>
        </Reveal>

        {/* Equipos y personal, con foto */}
        <Reveal sx={{ gridColumn: { sm: "1 / -1", md: "span 3" } }}>
          <Box
            sx={{
              ...celda,
              p: 0,
              overflow: "hidden",
              bgcolor: "#fff",
              border: `1px solid ${LINE}`,
            }}
          >
            <Box
              component="img"
              src={actividadesImg}
              alt="Coordinadores revisando los equipos en una tableta"
              loading="lazy"
              sx={{
                width: "100%",
                height: { xs: 200, md: 230 },
                objectFit: "cover",
                objectPosition: "center 35%",
              }}
            />
            <Box sx={{ p: { xs: 3, md: 3.5 } }}>
              <Typography
                component="h3"
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  color: INK,
                  mb: 1,
                }}
              >
                Equipos, entrenadores y jugadores
              </Typography>
              <Texto sx={{ fontSize: "0.98rem" }}>
                Equipos por deporte, categoría y temporada. Fichas completas y cambios de equipo sin
                perder el historial.
              </Texto>
            </Box>
          </Box>
        </Reveal>

        {/* Temporadas */}
        <Reveal delay={80} sx={{ gridColumn: { sm: "1 / -1", md: "span 3" } }}>
          <Box sx={{ ...celda, bgcolor: "#fff", border: `1px solid ${LINE}` }}>
            <Icono tono="navy">
              <HistoryIcon />
            </Icono>
            <TituloCelda>Cambio de temporada en un paso</TituloCelda>
            <Texto sx={{ fontSize: "0.98rem", mb: 3 }}>
              Al cerrar el año, la escuela entera pasa a la nueva temporada con sus jugadores y
              entrenadores. La anterior queda guardada para consultarla cuando quieras.
            </Texto>
            <Stack direction="row" spacing={1.25} sx={{ mt: "auto" }} alignItems="center">
              {["2024/25", "2025/26"].map((t) => (
                <Box
                  key={t}
                  sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: 999,
                    border: `1px solid ${LINE}`,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: MUTED,
                  }}
                >
                  {t}
                </Box>
              ))}
              <ArrowForwardIcon sx={{ color: MUTED, fontSize: 18 }} />
              <Box
                sx={{
                  px: 1.75,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor: NAVY,
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#fff",
                }}
              >
                2026/27
              </Box>
            </Stack>
          </Box>
        </Reveal>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- entrenos automáticos

const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie"];
const BLOQUES = [
  { dia: 0, top: 6, alto: 28, equipo: "Alevín A", hora: "17:30", tono: "ambar" },
  { dia: 0, top: 42, alto: 28, equipo: "Infantil", hora: "19:00", tono: "navy" },
  { dia: 1, top: 22, alto: 32, equipo: "Cadete F", hora: "18:00", tono: "gris" },
  { dia: 2, top: 6, alto: 28, equipo: "Alevín A", hora: "17:30", tono: "ambar" },
  { dia: 2, top: 42, alto: 28, equipo: "Infantil", hora: "19:00", tono: "navy" },
  { dia: 3, top: 22, alto: 32, equipo: "Cadete F", hora: "18:00", tono: "gris" },
  { dia: 4, top: 30, alto: 36, equipo: "Juvenil", hora: "18:30", tono: "navy" },
];
const TONOS_BLOQUE = {
  ambar: { bg: AMBER, fg: NAVY_DEEP },
  navy: { bg: NAVY, fg: "#fff" },
  gris: { bg: "#D9DEEA", fg: INK },
};

function VistaHorario() {
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "20px",
        border: `1px solid ${LINE}`,
        p: { xs: 2, sm: 2.5 },
        boxShadow: "0 30px 60px -30px rgba(19,29,61,0.3)",
      }}
    >
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: INK, mb: 2 }}>
        Horarios semanales
      </Typography>
      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: { xs: 0.75, sm: 1 } }}
      >
        {DIAS.map((d, i) => (
          <Box key={d}>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: MUTED,
                textAlign: "center",
                mb: 1,
              }}
            >
              {d}
            </Typography>
            <Box
              sx={{
                position: "relative",
                height: { xs: 200, sm: 240 },
                bgcolor: PAGE,
                borderRadius: "12px",
              }}
            >
              {BLOQUES.filter((b) => b.dia === i).map((b) => (
                <Box
                  key={`${b.dia}-${b.hora}`}
                  sx={{
                    position: "absolute",
                    left: 4,
                    right: 4,
                    top: `${b.top}%`,
                    height: `${b.alto}%`,
                    bgcolor: TONOS_BLOQUE[b.tono].bg,
                    color: TONOS_BLOQUE[b.tono].fg,
                    borderRadius: "10px",
                    p: 0.75,
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "0.58rem", sm: "0.66rem" },
                      fontWeight: 800,
                      lineHeight: 1.2,
                    }}
                  >
                    {b.equipo}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "0.55rem", sm: "0.62rem" },
                      fontWeight: 600,
                      opacity: 0.8,
                    }}
                  >
                    {b.hora}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const PASOS = [
  {
    icono: <EventRepeatIcon />,
    titulo: "Define el horario semanal",
    texto: "Una vez por temporada: qué días y a qué hora entrena cada equipo.",
  },
  {
    icono: <NotificationsActiveIcon />,
    titulo: "El entreno se crea solo",
    texto:
      "15 minutos antes de empezar aparece en la app y el entrenador recibe un aviso en el móvil.",
  },
  {
    icono: <FactCheckIcon />,
    titulo: "Pasa asistencia en la pista",
    texto:
      "Marca quién ha venido según llegan, o indica que se suspende. Las métricas se actualizan solas.",
  },
];

function ComoFunciona() {
  return (
    <Box
      component="section"
      id="como-funciona"
      sx={{
        bgcolor: "#fff",
        borderTop: `1px solid ${LINE}`,
        borderBottom: `1px solid ${LINE}`,
        scrollMarginTop: 68,
      }}
    >
      <Box
        sx={{
          ...contenedor,
          py: { xs: 8, md: 12 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1.1fr" },
          gap: { xs: 6, md: 10 },
          alignItems: "center",
        }}
      >
        <Box>
          <Reveal>
            <Etiqueta>Cómo funciona</Etiqueta>
            <Titulo sx={{ mb: 2 }}>Entrenamientos que se organizan solos</Titulo>
            <Texto sx={{ mb: 5 }}>Nadie tiene que acordarse de crear el entreno del día.</Texto>
          </Reveal>
          <Stack spacing={0}>
            {PASOS.map((p, i) => (
              <Reveal key={p.titulo} delay={i * 90}>
                <Stack
                  direction="row"
                  sx={{ position: "relative", gap: 2.5, pb: i < PASOS.length - 1 ? 4 : 0 }}
                >
                  {i < PASOS.length - 1 && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 21,
                        top: 52,
                        bottom: 8,
                        width: "2px",
                        bgcolor: LINE,
                        borderRadius: 1,
                      }}
                    />
                  )}
                  <Icono>{p.icono}</Icono>
                  <Box sx={{ pt: 0.5 }}>
                    <Typography
                      component="h3"
                      sx={{
                        fontSize: "1.15rem",
                        fontWeight: 800,
                        color: INK,
                        mb: 0.5,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {p.titulo}
                    </Typography>
                    <Texto sx={{ fontSize: "0.98rem" }}>{p.texto}</Texto>
                  </Box>
                </Stack>
              </Reveal>
            ))}
          </Stack>
        </Box>
        <Reveal delay={120} sx={{ position: "relative" }}>
          <VistaHorario />
          <AvisoPush
            sx={{
              position: { sm: "absolute" },
              mt: { xs: 2, sm: 0 },
              right: { sm: -12, md: -28 },
              bottom: { sm: -28 },
            }}
          />
        </Reveal>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- partidos

const JUGADORES_EJEMPLO = [
  { _id: "1", nombre: "Hugo Pérez", numero: 1 },
  { _id: "2", nombre: "Marco Ruiz", numero: 4 },
  { _id: "3", nombre: "Álvaro Gil", numero: 7 },
  { _id: "4", nombre: "Dani Romero", numero: 10 },
  { _id: "5", nombre: "Nico Vargas", numero: 9 },
  { _id: "6", nombre: "Leo Castro", numero: 6 },
  { _id: "7", nombre: "Iker Morales", numero: 11 },
];
const ALINEACION_EJEMPLO = [
  { slot: "portero", jugador_id: "1" },
  { slot: "cierre", jugador_id: "2" },
  { slot: "ala_1", jugador_id: "3" },
  { slot: "ala_2", jugador_id: "6" },
  { slot: "pivot", jugador_id: "5" },
];

function proximoSabado() {
  const d = new Date();
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7 || 7));
  return d.toISOString().slice(0, 10);
}

function Partidos() {
  const jugadoresById = useMemo(
    () => Object.fromEntries(JUGADORES_EJEMPLO.map((j) => [j._id, j])),
    []
  );
  const mensaje = useMemo(
    () =>
      construirMensajeConvocatoria({
        partido: {
          fecha: proximoSabado(),
          hora: "11:00",
          jornada: 6,
          equipo_rival: "CD Triana",
          local: true,
          lugar: "Pabellón San Pablo",
        },
        equipoNombre: "Alevín A",
        convocados: JUGADORES_EJEMPLO,
        antelacionMin: 45,
      }),
    []
  );

  return (
    <Box component="section" sx={{ ...contenedor, py: { xs: 8, md: 12 } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.15fr 1fr" },
          gap: { xs: 5, md: 8 },
          alignItems: "center",
        }}
      >
        <Reveal sx={{ order: { xs: 2, md: 1 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "0.95fr 1.1fr" },
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                bgcolor: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: "20px",
                p: 2,
                pointerEvents: "none",
              }}
              aria-hidden="true"
            >
              <Typography
                sx={{ fontSize: "0.85rem", fontWeight: 800, color: INK, mb: 1.5, px: 0.5 }}
              >
                Alineación del primer tiempo
              </Typography>
              <MedioCampoFutsal
                slots={SLOTS_FUTSAL}
                asignaciones={ALINEACION_EJEMPLO}
                jugadoresById={jugadoresById}
                onSlotClick={() => {}}
              />
            </Box>
            <Box
              sx={{
                borderRadius: "20px",
                overflow: "hidden",
                border: `1px solid ${LINE}`,
                boxShadow: "0 24px 48px -28px rgba(19,29,61,0.45)",
              }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ bgcolor: "#F0F2F5", px: 1.75, py: 1.25 }}
              >
                <Box
                  component="img"
                  src={silbato}
                  alt=""
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    p: 0.75,
                    objectFit: "contain",
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#111B21" }}>
                    Alevín A · Familias
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#667781" }}>
                    Entrenadores y familias del equipo
                  </Typography>
                </Box>
              </Stack>
              <MensajeWhatsApp texto={mensaje} hora="19:42" sx={{ borderRadius: 0 }} />
            </Box>
          </Box>
        </Reveal>
        <Reveal delay={100} sx={{ order: { xs: 1, md: 2 } }}>
          <Titulo sx={{ mb: 2 }}>Convocatorias listas para WhatsApp</Titulo>
          <Texto sx={{ mb: 2.5 }}>
            Elige a los convocados, colócalos en la pista de fútbol sala o voleibol y comparte el
            mensaje con la hora de llegada ya calculada.
          </Texto>
          <Texto>
            Si se acerca un partido sin convocatoria, Convokko te lo recuerda. Asistencia y
            resultados quedan registrados para las métricas.
          </Texto>
        </Reveal>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- beneficios

const BENEFICIOS = [
  {
    icono: <ScheduleIcon />,
    titulo: "Ahorro de tiempo",
    texto: "Lo repetitivo lo hace la app: entrenos, sincronización con la liga y recordatorios.",
  },
  {
    icono: <StorageIcon />,
    titulo: "Todo centralizado",
    texto: "Una sola fuente de verdad, desde cualquier dispositivo y organizada por temporadas.",
  },
  {
    icono: <InsightsIcon />,
    titulo: "Decisiones con datos",
    texto: "Asistencia y participación reales de cada jugador y cada equipo.",
  },
  {
    icono: <WorkspacePremiumIcon />,
    titulo: "Imagen profesional",
    texto: "Familias y jugadores ven un club organizado, puntual y bien comunicado.",
  },
];

function Beneficios() {
  return (
    <Box
      component="section"
      id="beneficios"
      sx={{ ...contenedor, pb: { xs: 8, md: 12 }, scrollMarginTop: 80 }}
    >
      <Reveal>
        <Titulo sx={{ mb: { xs: 5, md: 7 } }}>Menos papeleo, más deporte</Titulo>
      </Reveal>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
          columnGap: 4,
          rowGap: 5,
        }}
      >
        {BENEFICIOS.map((b, i) => (
          <Reveal key={b.titulo} delay={i * 80}>
            <Box sx={{ borderTop: `2px solid ${INK}`, pt: 3 }}>
              <Box sx={{ color: AMBER_DARK, display: "flex", mb: 2, "& svg": { fontSize: 30 } }}>
                {b.icono}
              </Box>
              <Typography
                component="h3"
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 800,
                  color: INK,
                  mb: 1,
                  letterSpacing: "-0.01em",
                }}
              >
                {b.titulo}
              </Typography>
              <Texto sx={{ fontSize: "0.98rem" }}>{b.texto}</Texto>
            </Box>
          </Reveal>
        ))}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- demo

const campoSx = {
  "& .MuiInputLabel-root": {
    position: "static",
    transform: "none",
    fontSize: "0.875rem",
    fontWeight: 700,
    color: "rgba(255,255,255,0.85)",
    mb: 0.75,
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
  "& .MuiInputLabel-root.Mui-error": { color: "#FCA5A5" },
  "& legend": { display: "none" },
  "& .MuiOutlinedInput-notchedOutline": { top: 0 },
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    color: "#fff",
    bgcolor: "rgba(255,255,255,0.06)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.18)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
    "&.Mui-focused fieldset": { borderColor: AMBER, borderWidth: 2 },
    "&.Mui-error fieldset": { borderColor: "#FCA5A5" },
  },
  "& .MuiFormHelperText-root": { mx: 0, mt: 0.75, fontSize: "0.8rem", fontWeight: 600 },
  "& .MuiFormHelperText-root.Mui-error": { color: "#FCA5A5" },
};

const FORM_VACIO = { nombre: "", email: "", telefono: "", escuela: "", mensaje: "" };
const emailValido = (v) => /\S+@\S+\.\S+/.test(v);

function Campo(props) {
  return <TextField fullWidth InputLabelProps={{ shrink: true }} sx={campoSx} {...props} />;
}

function Demo() {
  const notify = useNotify();
  const [form, setForm] = useState(FORM_VACIO);
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const cambiar = (campo) => (e) => {
    const valor = e.target.value;
    setForm((prev) => ({ ...prev, [campo]: valor }));
    setErrores((prev) => ({ ...prev, [campo]: "" }));
  };

  const enviar = async (e) => {
    e.preventDefault();
    const nuevos = {};
    if (!form.nombre.trim()) nuevos.nombre = "Dinos tu nombre";
    if (!form.email.trim()) nuevos.email = "Necesitamos un correo para contestarte";
    else if (!emailValido(form.email)) nuevos.email = "Revisa el correo, parece incompleto";
    if (!form.telefono.trim()) nuevos.telefono = "Necesitamos un teléfono de contacto";
    else if (!/^\d{9}$/.test(form.telefono.trim())) nuevos.telefono = "Debe tener 9 dígitos";
    if (!form.escuela.trim()) nuevos.escuela = "¿Cómo se llama tu escuela?";
    if (Object.keys(nuevos).length) {
      setErrores(nuevos);
      return;
    }

    setEnviando(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/externas/solicitar-cuenta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: form.nombre.trim(),
          email: form.email.trim(),
          telefono: form.telefono.trim(),
          escuela: form.escuela.trim(),
          mensaje: form.mensaje.trim() || null,
        }),
      });
      if (!res.ok) throw new Error();
      setEnviado(true);
      setForm(FORM_VACIO);
    } catch {
      notify.error(`No hemos podido enviar la solicitud. Escríbenos a ${EMAIL_CONTACTO}`);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box
      component="section"
      id="demo"
      sx={{ ...contenedor, pb: { xs: 3, md: 4 }, scrollMarginTop: 80 }}
    >
      <Box
        sx={{
          borderRadius: { xs: "24px", md: "28px" },
          bgcolor: NAVY,
          backgroundImage: `radial-gradient(90% 120% at 100% 0%, #26386F 0%, ${NAVY} 50%, ${NAVY_DEEP} 100%)`,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1.05fr" },
          gap: { xs: 5, md: 8 },
          p: { xs: 3, sm: 5, md: 7 },
        }}
      >
        <Reveal>
          <Titulo claro sx={{ mb: 2.5 }}>
            ¿Listo para transformar tu escuela deportiva?
          </Titulo>
          <Texto claro sx={{ mb: 4 }}>
            Diseñada para el deporte base, con las necesidades reales de coordinadores y
            entrenadores. Cuéntanos sobre tu escuela y te la enseñamos funcionando.
          </Texto>
          <Box
            component="img"
            src={equipoImg}
            alt="Equipo de coordinación de una escuela deportiva revisando Convokko"
            loading="lazy"
            sx={{
              display: { xs: "none", md: "block" },
              width: "100%",
              maxWidth: 440,
              aspectRatio: "4 / 3",
              objectFit: "cover",
              objectPosition: "center 30%",
              borderRadius: "20px",
            }}
          />
        </Reveal>

        <Reveal delay={100}>
          {enviado ? (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                py: 4,
              }}
            >
              <Icono tono="claro">
                <FactCheckIcon />
              </Icono>
              <Typography
                sx={{
                  fontSize: "1.6rem",
                  fontWeight: 800,
                  color: "#fff",
                  mt: 3,
                  mb: 1.5,
                  letterSpacing: "-0.02em",
                }}
              >
                ¡Solicitud recibida!
              </Typography>
              <Texto claro sx={{ mb: 3 }}>
                Te escribiremos en breve para concertar la demostración. Si tienes prisa, escríbenos
                a {EMAIL_CONTACTO}.
              </Texto>
              <Button onClick={() => setEnviado(false)} sx={botonFantasma}>
                Enviar otra solicitud
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={enviar} noValidate>
              <Stack spacing={2.5}>
                <Campo
                  label="Nombre completo"
                  value={form.nombre}
                  onChange={cambiar("nombre")}
                  error={!!errores.nombre}
                  helperText={errores.nombre}
                  autoComplete="name"
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2.5,
                  }}
                >
                  <Campo
                    type="email"
                    label="Correo electrónico"
                    value={form.email}
                    onChange={cambiar("email")}
                    error={!!errores.email}
                    helperText={errores.email}
                    autoComplete="email"
                  />
                  <Campo
                    type="tel"
                    label="Teléfono"
                    value={form.telefono}
                    onChange={cambiar("telefono")}
                    error={!!errores.telefono}
                    helperText={errores.telefono}
                    autoComplete="tel"
                    inputProps={{ inputMode: "numeric", maxLength: 9 }}
                  />
                </Box>
                <Campo
                  label="Nombre de tu escuela"
                  value={form.escuela}
                  onChange={cambiar("escuela")}
                  error={!!errores.escuela}
                  helperText={errores.escuela}
                  autoComplete="organization"
                />
                <Campo
                  label="¿Qué necesitas? (opcional)"
                  value={form.mensaje}
                  onChange={cambiar("mensaje")}
                  multiline
                  rows={3}
                />
                <Button
                  type="submit"
                  disabled={enviando}
                  sx={{
                    ...botonAmbar,
                    alignSelf: "flex-start",
                    minWidth: 220,
                    "&.Mui-disabled": { bgcolor: "rgba(251,181,63,0.5)", color: NAVY_DEEP },
                  }}
                >
                  {enviando ? (
                    <CircularProgress size={20} sx={{ color: NAVY_DEEP }} />
                  ) : (
                    "Solicitar demo"
                  )}
                </Button>
              </Stack>
            </Box>
          )}
        </Reveal>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------- pie

function Pie({ conSesion }) {
  return (
    <Box component="footer" sx={{ ...contenedor, py: { xs: 4, md: 5 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box component="img" src={logoConvokko} alt="Convokko" sx={{ height: 24 }} />
          <Typography sx={{ fontSize: "0.875rem", color: MUTED }}>
            © {new Date().getFullYear()}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={3} alignItems="center">
          <Box
            component="a"
            href={`mailto:${EMAIL_CONTACTO}`}
            sx={{
              color: MUTED,
              textDecoration: "none",
              fontSize: "0.9rem",
              fontWeight: 600,
              "&:hover": { color: INK },
            }}
          >
            {EMAIL_CONTACTO}
          </Box>
          <Box
            component="a"
            href={conSesion ? "/dashboard" : "/authentication/sign-in"}
            sx={{ color: INK, textDecoration: "none", fontSize: "0.9rem", fontWeight: 700 }}
          >
            {conSesion ? "Mi panel" : "Acceder"}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}

Pie.propTypes = { conSesion: PropTypes.bool.isRequired };

// ---------------------------------------------------------------- página

export default function Landing() {
  const conSesion = tieneSesion();

  useEffect(() => {
    const anterior = document.title;
    document.title = "Convokko | Gestión integral para escuelas deportivas";
    return () => {
      document.title = anterior;
    };
  }, []);

  return (
    <Box
      sx={{
        bgcolor: PAGE,
        color: INK,
        minHeight: "100dvh",
        overflowX: "clip",
        fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
        "& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiFormLabel-root, & .MuiFormHelperText-root":
          {
            fontFamily: "inherit",
          },
      }}
    >
      <Cabecera conSesion={conSesion} />
      <main>
        <Hero />
        <Cobertura />
        <Funciones />
        <ComoFunciona />
        <Partidos />
        <Beneficios />
        <Demo />
      </main>
      <Pie conSesion={conSesion} />
    </Box>
  );
}
